import React, {useMemo} from 'react';
import s from "./Form30DashboardFilter.module.css";
import sButtons from "../../../assets/styles/buttons.module.css";
import Select, {StylesConfig} from 'react-select';
import {DropdownIndicator} from "../../shared/DropdownIndicator";
import {FORM30_TABLES} from "../../../constants/form30Tables";

interface Form30DashboardFilterProps {
    selectedYear: number | null;
    onYearChange: (year: number | null) => void;
    selectedIndicator: string | null;
    onIndicatorChange: (indicator: string | null) => void;
    onApply: () => void;
}

interface SelectOption {
    readonly value: string;
    readonly label: string;
}

interface GroupedOption {
    readonly label: string;
    readonly options: readonly SelectOption[];
}

// Функция для создания группированных опций показателей
const createIndicatorOptions = (): GroupedOption[] => {
    return FORM30_TABLES.map(table => {
        const options: SelectOption[] = [];
        
        table.rows.forEach(row => {
            if (!row.isHeaderRow) {
                // Добавляем только строки с данными (не заголовки)
                const indicatorId = `${table.id}_${row.id}`;
                options.push({
                    value: indicatorId,
                    label: `${row.number} ${row.indicator}`
                });
            }
        });
        
        return {
            label: table.title,
            options
        };
    });
};

export const Form30DashboardFilter: React.FC<Form30DashboardFilterProps> = ({
    selectedYear,
    onYearChange,
    selectedIndicator,
    onIndicatorChange,
    onApply
}) => {
    // Генерируем опции для годов (от текущего года до 10 лет назад)
    const currentYear = new Date().getFullYear();
    const yearOptions: SelectOption[] = useMemo(() => {
        const years: SelectOption[] = [];
        const startYear = currentYear - 10;
        for (let year = currentYear; year >= startYear; year--) {
            years.push({
                value: year.toString(),
                label: year.toString()
            });
        }
        return years;
    }, [currentYear]);

    // Генерируем опции для показателей
    const indicatorOptions = useMemo(() => createIndicatorOptions(), []);

    // Находим выбранные опции
    const selectedYearOption = yearOptions.find(opt => opt.value === selectedYear?.toString()) || null;
    const selectedIndicatorOption = useMemo(() => {
        if (!selectedIndicator) return null;
        for (const group of indicatorOptions) {
            const found = group.options.find(opt => opt.value === selectedIndicator);
            if (found) return found;
        }
        return null;
    }, [selectedIndicator, indicatorOptions]);

    // Стили для селектов (как в SelectBasic)
    const customStyles: StylesConfig<SelectOption, false> = {
        control: (provided, state) => ({
            ...provided,
            cursor: state.isDisabled ? "not-allowed" : "pointer",
            borderRadius: '16px',
            backgroundColor: state.isDisabled ? "#E0E0E0" : state.hasValue ? "#FFF" : state.selectProps.menuIsOpen ? "#FFF" : "rgba(255, 255, 255, 0.10)",
            border: "none",
            padding: '16px 24px',
            minHeight: '56px',
            zIndex: state.isFocused ? 10 : 8,
            boxShadow: 'none',
            opacity: state.isDisabled ? 0.8 : 1,
            '&:hover': {
                borderColor: 'transparent',
            }
        }),
        option: (provided, state) => ({
            ...provided,
            padding: '0',
            color: '#363636',
            cursor: 'pointer',
            position: 'relative',
            fontSize: '16px',
            lineHeight: '130%',
            fontWeight: state.isSelected ? 500 : 400,
            borderBottom: '1px solid #AFAFAF',
            paddingBottom: '12px',
            paddingTop: '12px',
            backgroundColor: 'transparent',
            '&:last-child': {
                borderBottom: 'none',
                paddingBottom: 0
            },
            '&:first-of-type': {
                borderTop: 'none',
                paddingTop: 0
            },
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '0',
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '16px 24px',
            maxHeight: '600px',
        }),
        placeholder: (provided, state) => ({
            ...provided,
            color: state.hasValue ? '#363636' : state.selectProps.menuIsOpen ? '#363636' : 'rgba(255, 255, 255, 0.70)',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0',
        }),
        singleValue: (provided, state) => ({
            ...provided,
            padding: '0',
            fontFamily: '"Onest","Roboto", "Helvetica", "Arial", sans-serif',
            color: state.isDisabled ? "#999" : "#363636",
            fontSize: '16px',
            fontWeight: 500,
            margin: 0
        }),
        group: (provided) => ({
            ...provided,
            paddingTop: '12px',
            paddingBottom: '12px',
        }),
        groupHeading: (provided) => ({
            ...provided,
            fontSize: '14px',
            fontWeight: 600,
            color: '#1A64A5',
            textTransform: 'none',
            marginBottom: '8px',
        }),
    };

    const isButtonDisabled = !selectedYear || !selectedIndicator;

    return (
        <div className={s.filterWrapper}>
            <Select<SelectOption>
                value={selectedYearOption}
                onChange={(newValue) => {
                    onYearChange(newValue ? Number(newValue.value) : null);
                }}
                options={yearOptions}
                isClearable={false}
                isSearchable={false}
                placeholder="Выберите год"
                noOptionsMessage={() => "Нет доступных опций"}
                styles={{
                    ...customStyles,
                    control: (provided, state) => ({
                        ...(customStyles.control?.(provided, state) || provided),
                        padding: '20.5px 24px',
                    }),
                }}
                components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator
                }}

            />
            <Select<SelectOption>
                value={selectedIndicatorOption}
                onChange={(newValue) => {
                    onIndicatorChange(newValue ? newValue.value : null);
                }}
                options={indicatorOptions}
                isClearable={false}
                isSearchable={true}
                placeholder="Выберите показатель"
                noOptionsMessage={() => "Нет доступных опций"}
                styles={customStyles}
                components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator
                }}
            />
            <button 
                className={sButtons.mainButton} 
                disabled={isButtonDisabled} 
                onClick={onApply}
            >
                Применить
            </button>
        </div>
    );
};
